import {
  Text,
  Icon,
  Flex,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Avatar,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Tooltip,
} from '@chakra-ui/react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useRef } from 'react';
import * as yup from 'yup';
import { useShowToast } from '@/hooks/useShowToast';

import { Restart } from '@/assets/icons/line';
import { useRestartOutreachMutation } from '@/store/query/outreachCandidates';
import { useParams } from 'react-router-dom';

export const contactSchema = yup.object({
  email: yup.string().email('Email is not valid').required('Email is required'),
});

export const RestartModal = ({
  isOpen = false,
  onClose = () => {},
  contact = {},
}) => {
  const { outreachId } = useParams();
  const formRef = useRef();
  const toast = useShowToast();
  const [restartOutreach, { isLoading }] = useRestartOutreachMutation();
  const onSubmit = async (values) => {
    try {
      const candidates = [{ candidate_id: contact?.id, email: values?.email }];
      const result = await restartOutreach({ candidates, outreachId });
      if (result?.data?.[0]?.errors?.length) {
        toast({
          title: 'Flow cannot be re-started.',
          description: result?.data?.[0]?.errors[0],
          position: 'bottom-left',
          status: 'error',
          isClosable: true,
        });
        return;
      }
      onClose();
      toast({
        title: 'Flow re-started successfully.',
        position: 'bottom-left',
        status: 'success',
        isClosable: true,
      });
    } catch {}
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          <Flex>
            <Avatar
              className="avatar-md"
              name={contact?.full_name}
              src={contact?.avatar}
              width="28px"
              height="28px"
            />
            <Text size="lg-semi-bold" ml="8px">
              {contact?.full_name}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Formik
            validateOnBlur={false}
            innerRef={formRef}
            initialValues={{
              email: '',
            }}
            validationSchema={contactSchema}
            onSubmit={onSubmit}
          >
            {(props) => (
              <Form noValidate>
                <Field name="email">
                  {({ field, form }) => (
                    <FormControl
                      mb="16px"
                      isRequired
                      isInvalid={form.errors.email && form.touched.email}
                    >
                      <FormLabel
                        fontWeight="semiBold"
                        fontSize="14px"
                        htmlFor="name"
                      >
                        Email address
                      </FormLabel>
                      <Input
                        {...field}
                        id="email"
                        placeholder="abc@example.com"
                      />
                      <FormErrorMessage>
                        <ErrorMessage name="email" />
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <ModalFooter pr="0" mr="0">
                  <Button variant="secondary" mr="8px" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    leftIcon={<Restart />}
                    type="submit"
                    isLoading={isLoading}
                    loadingText="Saving.."
                  >
                    Save & Restart Flow
                  </Button>
                </ModalFooter>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export const RestartOutreach = ({ contact }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <Flex alignItems="center">
      <Tooltip
        label="The contact info couldn't be enriched by Kula. You can enter the contact details manually and restart the Flow."
        arrowSize={8}
        hasArrow
        placement="top"
        borderRadius="4px"
      >
        <Flex>
          <Icon
            as={Restart}
            onClick={onOpen}
            bg="#696B6C"
            w="20px"
            h="20px"
            borderRadius="full"
            _hover={{ bg: '#0043CE' }}
          />
        </Flex>
      </Tooltip>
      {isOpen && (
        <RestartModal isOpen={isOpen} onClose={onClose} contact={contact} />
      )}
    </Flex>
  );
};
